"use client";

import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../lib/utill";
import { Button } from "antd";

export default function MiniCalendar({ current, setCurrent, setView, data }) {
  const today = new Date();
  const todayDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  const [currentMonth, setCurrentMonth] = useState(
    current ? new Date(current) : todayDate
  );
  const [selectedDate, setSelectedDate] = useState(
    current ? new Date(current).getDate() : todayDate.getDate()
  );

  useEffect(() => {
    if (current) {
      const currentDateObj = new Date(current);
      setCurrentMonth(
        new Date(currentDateObj.getFullYear(), currentDateObj.getMonth(), 1)
      );
      setSelectedDate(currentDateObj.getDate());
    }
  }, [current]);

  const daysOfWeek = ["S", "M", "T", "W", "T", "F", "S"];

  const getMonthData = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const monthName = date.toLocaleString("default", { month: "long" });
    const yearNum = date.getFullYear();

    return { firstDay, daysInMonth, monthName, yearNum };
  };

  const { firstDay, daysInMonth, monthName, yearNum } =
    getMonthData(currentMonth);

  const prevMonth = () => {
    const prev = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() - 1,
      1
    );
    if (
      prev.getFullYear() < todayDate.getFullYear() ||
      (prev.getFullYear() === todayDate.getFullYear() &&
        prev.getMonth() < todayDate.getMonth())
    ) {
      return;
    }

    setCurrentMonth(prev);
    const daysInPrevMonth = new Date(
      prev.getFullYear(),
      prev.getMonth() + 1,
      0
    ).getDate();
    const newSelectedDate = Math.min(selectedDate, daysInPrevMonth);
    setSelectedDate(newSelectedDate);
    //setCurrent(new Date(prev.getFullYear(), prev.getMonth(), newSelectedDate));
    setView("month");
  };

  const nextMonth = () => {
    const next = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      1
    );
    setCurrentMonth(next);
    const daysInNextMonth = new Date(
      next.getFullYear(),
      next.getMonth() + 1,
      0
    ).getDate();
    const newSelectedDate = Math.min(selectedDate, daysInNextMonth);
    setSelectedDate(newSelectedDate);
    // setCurrent(new Date(next.getFullYear(), next.getMonth(), newSelectedDate));
    setView("month");
  };

  const handleDateClick = (day) => {
    if (day === null) return;
    const clickedDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );
    if (clickedDate < todayDate) return;
    setSelectedDate(day);
    setCurrent(clickedDate);
    setView("week");
  };

  const days = [];
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  const isPastDay = (day) => {
    if (day === null) return false;
    const dayDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );
    return dayDate < todayDate;
  };

  const isPrevMonthDisabled =
    currentMonth.getFullYear() < todayDate.getFullYear() ||
    (currentMonth.getFullYear() === todayDate.getFullYear() &&
      currentMonth.getMonth() <= todayDate.getMonth());

  const hasScheduleForDate = (day) => {
    if (day === null) return false;

    const dateObj = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );

    // Convert dateObj to 'YYYY-MM-DD' in local time
    const dateStr = dateObj.toLocaleDateString("en-CA"); // en-CA gives 'YYYY-MM-DD' in local time

    // Flatten all schedule entries into one array
    const allSchedules = Object.values(data?.dailySchedule || {}).flat();

    // Check if any schedule has a startDate equal to dateStr
    return allSchedules.some((entry) => entry.startDate === dateStr);
  };

  return (
    <div className="w-full bg-[#FFFFFF] p-0">
      <div className="mb-3 flex items-center justify-between">
        <Button
          variant="outline"
          size="icon"
          onClick={prevMonth}
          className={cn(
            "flex items-center justify-center !rounded-lg !border-[#E2E8F0] shadow-sm",
            isPrevMonthDisabled && "cursor-not-allowed opacity-50"
          )}
          disabled={isPrevMonthDisabled}
        >
          <ChevronLeft className="h-4 w-4 text-[#334155]" />
        </Button>
        <h3 className="text-sm font-semibold text-[#171717] font-poppins mb-0">
          {monthName} {yearNum}
        </h3>
        <Button
          variant="outline"
          size="icon"
          onClick={nextMonth}
          className="flex items-center justify-center !rounded-lg !border-[#E2E8F0] shadow-sm"
        >
          <ChevronRight className="h-4 w-4 text-[#334155]" />
        </Button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center justify-items-center">
        {daysOfWeek.map((day, index) => (
          <div key={index} className="text-[11px] font-semibold text-[#64748B] font-poppins">
            {day}
          </div>
        ))}

        {days.map((day, index) => {
          const disabled = isPastDay(day);
          const hasSchedule = hasScheduleForDate(day);

          return (
            <div
              key={index}
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded-lg text-[11px] relative transition-colors",
                day === null
                  ? "invisible"
                  : disabled
                  ? "cursor-not-allowed text-[#CBD5E1]"
                  : "cursor-pointer",
                day === selectedDate && !disabled
                  ? "bg-[#0F172A] text-white font-semibold font-poppins shadow-md"
                  : "hover:bg-[#F1F5F9] text-[#171717] font-poppins"
              )}
              onClick={() => !disabled && day !== null && handleDateClick(day)}
            >
              <div
                className={cn(
                  "flex h-7 w-7 items-center justify-center rounded-lg",
                  hasSchedule && day !== selectedDate
                    ? "bg-[#D1FAE5] text-[#047857] font-semibold"
                    : "",
                  disabled ? "text-[#CBD5E1]" : ""
                )}
              >
                {day}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
